import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { TalkRoomFolder, TalkRoomFile } from '../../types';
import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';

interface FoldersTabProps {
  roomId: string;
}

export default function FoldersTab({ roomId }: FoldersTabProps) {
  const [folders, setFolders] = useState<TalkRoomFolder[]>([]);
  const [files, setFiles] = useState<TalkRoomFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string; name: string }>>([]);
  
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<TalkRoomFolder | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; type: 'image' | 'pdf' } | null>(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<TalkRoomFolder | null>(null);
  const [deleteFileTarget, setDeleteFileTarget] = useState<TalkRoomFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchData();
  }, [roomId, currentFolderId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const folderQuery = currentFolderId ? `?parentId=${currentFolderId}` : '';
      const fileQuery = currentFolderId ? `?folderId=${currentFolderId}` : '';

      const [foldersData, filesData] = await Promise.all([
        api.get<{ items: TalkRoomFolder[] }>(`/api/talkrooms/${roomId}/folders${folderQuery}`),
        api.get<{ items: TalkRoomFile[] }>(`/api/talkrooms/${roomId}/files${fileQuery}`),
      ]);

      setFolders(foldersData.items || []);
      setFiles(filesData.items || []);

      // パンくずリスト更新
      if (currentFolderId) {
        const folderDetail = await api.get<TalkRoomFolder & { breadcrumbs: Array<{ id: string; name: string }> }>(
          `/api/talkrooms/${roomId}/folders/${currentFolderId}`
        );
        setBreadcrumbs(folderDetail.breadcrumbs || []);
      } else {
        setBreadcrumbs([]);
      }
    } catch (error) {
      console.error('Failed to fetch folders/files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim() || saving) return;

    setSaving(true);
    try {
      await api.post(`/api/talkrooms/${roomId}/folders`, {
        name: newFolderName.trim(),
        parentId: currentFolderId,
      });
      setNewFolderName('');
      setShowCreateFolderModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to create folder:', error);
      toast.error('フォルダの作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleRenameFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolder || !newFolderName.trim() || saving) return;

    setSaving(true);
    try {
      await api.put(`/api/talkrooms/${roomId}/folders/${selectedFolder.id}`, {
        name: newFolderName.trim(),
      });
      setShowRenameFolderModal(false);
      setSelectedFolder(null);
      setNewFolderName('');
      fetchData();
    } catch (error) {
      console.error('Failed to rename folder:', error);
      toast.error('フォルダ名の変更に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async (folder: TalkRoomFolder) => {
    setDeleteFolderTarget(folder);
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    try {
      await api.delete(`/api/talkrooms/${roomId}/folders/${deleteFolderTarget.id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete folder:', error);
      toast.error('フォルダの削除に失敗しました');
    } finally {
      setDeleteFolderTarget(null);
    }
  };

  const handleDeleteFile = async (file: TalkRoomFile) => {
    setDeleteFileTarget(file);
  };

  const confirmDeleteFile = async () => {
    if (!deleteFileTarget) return;
    try {
      await api.delete(`/api/talkrooms/${roomId}/files/${deleteFileTarget.id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error('ファイルの削除に失敗しました');
    } finally {
      setDeleteFileTarget(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    setUploading(true);
    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        if (file.size > 50 * 1024 * 1024) {
          toast.error(`${file.name}: ファイルサイズは50MB以下にしてください`);
          continue;
        }

        const url = await api.uploadFile(file, { entityType: 'talkroom-folder', entityId: roomId });
        
        await api.post(`/api/talkrooms/${roomId}/files`, {
          fileName: file.name,
          fileUrl: url,
          fileType: file.type,
          fileSize: file.size,
          folderId: currentFolderId,
        });
      }
      fetchData();
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('ファイルのアップロードに失敗しました');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const navigateToFolder = (folderId: string | null) => {
    setCurrentFolderId(folderId);
  };

  const openRenameModal = (folder: TalkRoomFolder) => {
    setSelectedFolder(folder);
    setNewFolderName(folder.name);
    setShowRenameFolderModal(true);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isImageFile = (fileType?: string, fileName?: string) => {
    const type = fileType?.toLowerCase() || '';
    const name = fileName?.toLowerCase() || '';
    return type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/.test(name);
  };

  const isPdfFile = (fileType?: string, fileName?: string) => {
    const type = fileType?.toLowerCase() || '';
    const name = fileName?.toLowerCase() || '';
    return type === 'application/pdf' || name.endsWith('.pdf');
  };

  const getFileIcon = (fileType?: string, fileName?: string, fileUrl?: string) => {
    const type = fileType?.toLowerCase() || '';
    const name = fileName?.toLowerCase() || '';

    if (isImageFile(fileType, fileName) && fileUrl) {
      return (
        <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
          <img
            src={fileUrl}
            alt={fileName || ''}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              // 画像読み込み失敗時はアイコンにフォールバック
              const target = e.currentTarget;
              target.style.display = 'none';
              target.parentElement!.innerHTML = '<svg class="w-8 h-8 text-green-500 m-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>';
            }}
          />
        </div>
      );
    }

    if (type === 'application/pdf' || name.endsWith('.pdf')) {
      return (
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }

    if (/\.(doc|docx)$/.test(name) || type.includes('word')) {
      return (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }

    if (/\.(xls|xlsx|csv)$/.test(name) || type.includes('excel') || type.includes('spreadsheet')) {
      return (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }

    return (
      <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* ヘッダー */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-white">フォルダ</h3>
          <div className="flex gap-2">
            <Button
              onClick={() => { setShowCreateFolderModal(true); setNewFolderName(''); }}
              size="sm"
              variant="secondary"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              新規フォルダ
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              size="sm"
              disabled={uploading}
            >
              {uploading ? (
                'アップロード中...'
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  アップロード
                </>
              )}
            </Button>
          </div>
        </div>

        {/* パンくずリスト */}
        <div className="flex items-center gap-1 text-sm">
          <button
            onClick={() => navigateToFolder(null)}
            className={`hover:text-blue-600 ${!currentFolderId ? 'font-medium text-blue-600' : 'text-gray-600 dark:text-gray-400'}`}
          >
            ルート
          </button>
          {breadcrumbs.map((crumb, idx) => (
            <div key={crumb.id} className="flex items-center gap-1">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <button
                onClick={() => navigateToFolder(crumb.id)}
                className={`hover:text-blue-600 ${
                  idx === breadcrumbs.length - 1
                    ? 'font-medium text-blue-600'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {crumb.name}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ファイル・フォルダ一覧 */}
      <div className="flex-1 overflow-y-auto p-4">
        {folders.length === 0 && files.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-12">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <p>このフォルダは空です</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* フォルダ */}
            {folders.map((folder) => (
              <div
                key={folder.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 group"
              >
                <button
                  onClick={() => navigateToFolder(folder.id)}
                  className="flex items-center gap-3 flex-1 min-w-0"
                >
                  <svg className="w-8 h-8 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{folder.name}</p>
                    <p className="text-xs text-gray-500">{folder.createdByName} • {formatDate(folder.createdAt)}</p>
                  </div>
                </button>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openRenameModal(folder)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                    title="名前を変更"
                  >
                    <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteFolder(folder)}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                    title="削除"
                  >
                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            {/* ファイル */}
            {files.map((file) => {
              const isImg = isImageFile(file.fileType, file.fileName);
              const isPdf = isPdfFile(file.fileType, file.fileName);
              const isPreviewable = isImg || isPdf;
              return (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 group"
              >
                {isPreviewable ? (
                  <button
                    onClick={() => setPreviewFile({
                      url: file.fileUrl,
                      name: file.fileName,
                      type: isImg ? 'image' : 'pdf',
                    })}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    {getFileIcon(file.fileType, file.fileName, file.fileUrl)}
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate hover:text-blue-600">{file.fileName}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.fileSize)} • {file.uploadedByName} • {formatDate(file.uploadedAt)}
                      </p>
                    </div>
                  </button>
                ) : (
                  <a
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    {getFileIcon(file.fileType, file.fileName, file.fileUrl)}
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate hover:text-blue-600">{file.fileName}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.fileSize)} • {file.uploadedByName} • {formatDate(file.uploadedAt)}
                      </p>
                    </div>
                  </a>
                )}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a
                    href={file.fileUrl}
                    download={file.fileName}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                    title="ダウンロード"
                  >
                    <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </a>
                  <button
                    onClick={() => handleDeleteFile(file)}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                    title="削除"
                  >
                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 新規フォルダモーダル */}
      <Modal isOpen={showCreateFolderModal} onClose={() => setShowCreateFolderModal(false)} title="新しいフォルダ">
        <form onSubmit={handleCreateFolder} className="space-y-4">
          <Input
            label="フォルダ名"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            required
            placeholder="フォルダ名を入力"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setShowCreateFolderModal(false)}>
              キャンセル
            </Button>
            <Button type="submit" disabled={saving || !newFolderName.trim()}>
              {saving ? '作成中...' : '作成'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* フォルダ名変更モーダル */}
      <Modal isOpen={showRenameFolderModal} onClose={() => setShowRenameFolderModal(false)} title="フォルダ名を変更">
        <form onSubmit={handleRenameFolder} className="space-y-4">
          <Input
            label="フォルダ名"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            required
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setShowRenameFolderModal(false)}>
              キャンセル
            </Button>
            <Button type="submit" disabled={saving || !newFolderName.trim()}>
              {saving ? '変更中...' : '変更'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* フォルダ削除確認モーダル */}
      <Modal isOpen={!!deleteFolderTarget} onClose={() => setDeleteFolderTarget(null)} title="フォルダの削除">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            フォルダ「<span className="font-medium">{deleteFolderTarget?.name}</span>」を削除しますか？中のファイルも削除されます。
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteFolderTarget(null)}>キャンセル</Button>
            <Button variant="danger" onClick={confirmDeleteFolder}>削除</Button>
          </div>
        </div>
      </Modal>

      {/* ファイル削除確認モーダル */}
      <Modal isOpen={!!deleteFileTarget} onClose={() => setDeleteFileTarget(null)} title="ファイルの削除">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            ファイル「<span className="font-medium">{deleteFileTarget?.fileName}</span>」を削除しますか？
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteFileTarget(null)}>キャンセル</Button>
            <Button variant="danger" onClick={confirmDeleteFile}>削除</Button>
          </div>
        </div>
      </Modal>

      {/* ファイルプレビューモーダル（画像・PDF） */}
      {previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setPreviewFile(null)}
        >
          {/* 閉じるボタン */}
          <button
            onClick={() => setPreviewFile(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full transition-colors z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* ファイル名 */}
          <div className="absolute top-4 left-4 text-white/80 text-sm bg-black/40 px-3 py-1.5 rounded-lg flex items-center gap-2">
            {previewFile.type === 'pdf' && (
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            )}
            {previewFile.name}
          </div>

          {/* コンテンツ: 画像 or PDF */}
          {previewFile.type === 'image' ? (
            <img
              src={previewFile.url}
              alt={previewFile.name}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div
              className="w-[90vw] h-[85vh] bg-white rounded-lg shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={previewFile.url}
                title={previewFile.name}
                className="w-full h-full border-0"
              />
            </div>
          )}

          {/* ダウンロード・新しいタブで開くボタン */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
            <a
              href={previewFile.url}
              download={previewFile.name}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors backdrop-blur-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              ダウンロード
            </a>
            <a
              href={previewFile.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors backdrop-blur-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              新しいタブで開く
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
